import { useInfiniteQuery, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  acceptRecommendation,
  getPerformance,
  getRecommendations,
  getRecommendationsOverview,
  rejectRecommendation
} from './api';
import { RecommendationsOverview } from './types';
import { queryClient } from '@/providers/AllProviders';

const RECOMMENDATIONS_PAGE_SIZE = 5;

type AcceptRecommendationPayload = {
  id?: string;
  question?: string;
  answer?: string;
  destination_type: 'agent' | 'business';
  knowledge_base_id?: number;
};

export function useGetPerformance(params?: Record<string, any>, options?: UseQueryOptions<any, Error>) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<any>({
    queryKey: ['performance', locationId, agentId, params],
    queryFn: () => getPerformance(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId && params?.start_date),
    ...options
  });
}

export function useInfiniteGetRecommendations(params?: Record<string, any>) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();
  const queryParams = { ...params, per_page: RECOMMENDATIONS_PAGE_SIZE };

  return useInfiniteQuery({
    queryKey: ['recommendations', locationId, agentId, 'infinite', queryParams],
    queryFn: ({ pageParam }) =>
      getRecommendations(locationId!, agentId!, {
        ...queryParams,
        page: pageParam
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = lastPage.total_pages;
      if (typeof totalPages === 'number') {
        return allPages.length < totalPages ? allPages.length + 1 : undefined;
      }
      if ((lastPage.data?.length ?? 0) < RECOMMENDATIONS_PAGE_SIZE) return undefined;
      return allPages.length + 1;
    },
    enabled: Boolean(locationId && agentId)
  });
}

export function useGetRecommendationsOverview(options?: UseQueryOptions<RecommendationsOverview, Error>) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<RecommendationsOverview>({
    queryKey: ['recommendations-overview', locationId, agentId],
    queryFn: () => getRecommendationsOverview(locationId!, agentId!),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}

export function useAcceptRecommendation(onClose: any) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useMutation({
    mutationFn: (data: AcceptRecommendationPayload) => acceptRecommendation(locationId!, agentId!, data),
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: ['recommendations', locationId, agentId]
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendations-overview', locationId, agentId]
      });
    }
  });
}

export function useRejectRecommendation() {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: string) => rejectRecommendation(locationId!, agentId!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['recommendations', locationId, agentId]
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendations-overview', locationId, agentId]
      });
    }
  });
}
